import { Controller } from "@hotwired/stimulus"

// QAN-527: lets a Lender product be filed under an existing bank's Lender ID
// (BL####) instead of always minting a new one.
//
// The bank directory (~300 entries, one per BL) ships inline in the banks value
// rather than behind a search endpoint — it is a few KB and the whole point is
// instant filtering. The hidden existing_lender_external_id field is the only
// thing the server reads; everything else here is preview, so the form still
// saves correctly if any of this fails.
const MAX_RESULTS = 50

export default class extends Controller {
  static targets = [
    "modeRadio", "existingPanel", "search", "results", "selectedId",
    "nameField", "nameHint", "lenderIdPreview", "lenderIdHint"
  ]
  static values = { banks: Array }

  connect() {
    this.matches = []
    this.activeIndex = -1
    this.originalLenderId = this.lenderIdPreviewTarget.value
    // The last name the admin could actually edit. "Existing" mode overwrites the
    // field with the chosen bank's name, so this is what gets put back when they
    // switch away again — without it, anything typed before the switch was lost.
    this.enteredLenderName = this.nameFieldTarget.value

    // Clicking anywhere outside the combobox dismisses the result list. Stored so
    // disconnect can detach it — the form partial is re-rendered on validation
    // failures, so a leaked listener would accumulate.
    this.outsideClickHandler = (event) => {
      if (!this.element.contains(event.target)) this.closeResults()
    }
    document.addEventListener("click", this.outsideClickHandler)
  }

  disconnect() {
    document.removeEventListener("click", this.outsideClickHandler)
  }

  get mode() {
    const checked = this.modeRadioTargets.find(radio => radio.checked)
    return checked ? checked.value : null
  }

  modeChanged() {
    const mode = this.mode
    const attachingExisting = mode === "existing"

    // Capture before the branches below overwrite the field. Only while it is
    // editable is the value the admin's own, rather than a copied bank name.
    if (!this.nameFieldTarget.readOnly) this.enteredLenderName = this.nameFieldTarget.value

    this.existingPanelTarget.classList.toggle("hidden", !attachingExisting)
    if (!attachingExisting) this.closeResults()

    this.nameFieldTarget.readOnly = attachingExisting
    this.nameHintTarget.classList.toggle("hidden", !attachingExisting)
    this.setFieldStyle(this.nameFieldTarget, attachingExisting)

    if (attachingExisting) {
      const bank = this.selectedBank()
      this.nameFieldTarget.value = bank ? bank.name : ""
      this.lenderIdPreviewTarget.value = bank ? bank.external_id : ""
      this.setLenderIdHint("kept — same lender", false)
    } else if (mode === "new") {
      this.nameFieldTarget.value = this.enteredLenderName
      this.lenderIdPreviewTarget.value = ""
      this.setLenderIdHint("new — auto-generated", true)
    } else {
      this.nameFieldTarget.value = this.enteredLenderName
      this.lenderIdPreviewTarget.value = this.originalLenderId
      this.setLenderIdHint("unchanged", false)
    }

    this.announceBank()
  }

  // The hint is colour-coded, not just worded: green when the Lender ID is carried
  // over from the chosen lender, indigo when it will be generated on save. Text and
  // colour must move together or the two disagree after a mode switch.
  setLenderIdHint(text, generated) {
    this.lenderIdHintTarget.textContent = text
    this.lenderIdHintTarget.classList.toggle("text-indigo-600", generated)
    this.lenderIdHintTarget.classList.toggle("text-green-600", !generated)
  }

  search() {
    const query = this.searchTarget.value.trim().toLowerCase()

    // An empty box lists the first page of banks so the picker is usable without
    // knowing what to type.
    this.matches = (query === ""
      ? this.banksValue
      : this.banksValue.filter(bank =>
          bank.name.toLowerCase().includes(query) ||
          bank.external_id.toLowerCase().includes(query)
        )
    ).slice(0, MAX_RESULTS)

    this.activeIndex = this.matches.length > 0 ? 0 : -1
    this.renderResults()
  }

  keydown(event) {
    if (event.key === "Escape") {
      this.closeResults()
      return
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      if (this.matches.length === 0) {
        this.search()
        return
      }
      const step = event.key === "ArrowDown" ? 1 : -1
      this.activeIndex = (this.activeIndex + step + this.matches.length) % this.matches.length
      this.highlightActive()
      this.openResults()
      return
    }

    // Enter must not submit the whole form while the user is picking a lender.
    if (event.key === "Enter") {
      if (this.activeIndex >= 0 && this.matches[this.activeIndex]) {
        event.preventDefault()
        this.choose(this.matches[this.activeIndex])
      }
    }
  }

  renderResults() {
    this.resultsTarget.innerHTML = ""

    if (this.matches.length === 0) {
      const empty = document.createElement("li")
      empty.className = "px-3 py-2 text-gray-500"
      empty.textContent = "No lenders match that search"
      this.resultsTarget.appendChild(empty)
      this.openResults()
      return
    }

    this.matches.forEach((bank, index) => {
      const item = document.createElement("li")
      item.className = "px-3 py-2 cursor-pointer flex justify-between gap-3"
      item.setAttribute("role", "option")

      // textContent throughout: lender names are admin-entered free text and must
      // never be interpolated as markup.
      const label = document.createElement("span")
      label.className = "text-gray-900"
      label.textContent = bank.name

      const meta = document.createElement("span")
      meta.className = "text-gray-500 whitespace-nowrap"
      meta.textContent = `${bank.external_id} · ${bank.product_count} product${bank.product_count === 1 ? "" : "s"}`

      item.appendChild(label)
      item.appendChild(meta)
      // mousedown, not click: the input's blur would otherwise fire first and the
      // outside-click handler would tear the list down before the click landed.
      item.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.choose(bank)
      })
      item.addEventListener("mouseenter", () => {
        this.activeIndex = index
        this.highlightActive()
      })

      this.resultsTarget.appendChild(item)
    })

    this.highlightActive()
    this.openResults()
  }

  // Moves the highlight without rebuilding the list, so arrow keys and mouse
  // movement don't churn through DOM nodes and their listeners.
  highlightActive() {
    Array.from(this.resultsTarget.children).forEach((item, index) => {
      const active = index === this.activeIndex
      item.classList.toggle("bg-indigo-50", active)
      item.setAttribute("aria-selected", active ? "true" : "false")
      if (active) item.scrollIntoView({ block: "nearest" })
    })
  }

  choose(bank) {
    this.selectedIdTarget.value = bank.external_id
    this.searchTarget.value = `${bank.name} — Lender ID ${bank.external_id}`
    this.nameFieldTarget.value = bank.name
    this.lenderIdPreviewTarget.value = bank.external_id
    this.setLenderIdHint("kept — same lender", false)
    this.closeResults()
    this.announceBank()
  }

  // QAN-698: tells the rest of the form which bank a new product is being filed
  // under, so bank-level fields can be pre-populated. Fired from BOTH choose()
  // and modeChanged(): without the latter, "pick bank A, then switch to Create a
  // new lender" would mint a brand-new bank still carrying A's data.
  //
  // The mode travels with the event because a bank id alone can't say whether it
  // is still in force. Stimulus dispatch bubbles, and every listener sits inside
  // the same form, so no window-level broadcast is needed.
  announceBank() {
    this.dispatch("bankChanged", {
      detail: { mode: this.mode, externalId: this.selectedIdTarget.value }
    })
  }

  selectedBank() {
    const id = this.selectedIdTarget.value
    return this.banksValue.find(bank => bank.external_id === id) || null
  }

  openResults() {
    this.resultsTarget.classList.remove("hidden")
    this.searchTarget.setAttribute("aria-expanded", "true")
  }

  closeResults() {
    this.resultsTarget.classList.add("hidden")
    this.searchTarget.setAttribute("aria-expanded", "false")
    this.activeIndex = -1
  }

  setFieldStyle(field, readOnly) {
    const readOnlyClasses = ["bg-gray-100", "text-gray-500", "cursor-not-allowed"]
    const editableClasses = ["focus:border-indigo-500", "focus:ring-indigo-500"]
    readOnlyClasses.forEach(cls => field.classList.toggle(cls, readOnly))
    editableClasses.forEach(cls => field.classList.toggle(cls, !readOnly))
  }
}
